/**
 * C4, this tab's report as a sandbox host candidate: sent with `hello` and `presence.update` as
 * `executor`, on change and debounced (CANDIDATE_DEBOUNCE_MS). The server elects one host per user
 * from the reports (C5, apps.sandbox.routing); K0 declares the shape (core/hostCalls.ts).
 *
 * Any reason makes the tab no candidate (score 0): phones, cellular links, Save-Data, a low battery,
 * low memory, a hidden tab not used lately, and no WebAssembly. Those never download the toolchain;
 * the hosted host serves them. Among candidates the score ranks them. A tab whose sandbox failed to
 * start (markBootFailed) is no candidate either, for the rest of its life, and says so at once.
 */
import type { Candidate, CandidateReason } from "../core/hostCalls";

export type { Candidate, CandidateReason } from "../core/hostCalls";

export const CANDIDATE_REASONS = [
  "phone",
  "cellular",
  "save_data",
  "low_battery",
  "low_memory",
  "hidden",
  "no_wasm",
  "boot_failed",
] as const satisfies readonly CandidateReason[];

/** A change is reported this long after the last one, so a burst of changes sends one update. */
export const CANDIDATE_DEBOUNCE_MS = 5_000;
/** A hidden tab used this recently is still a candidate: a brief switch away does not move work. */
export const RECENT_MS = 60_000;
export const LOW_BATTERY = 0.2;
/** GB, as navigator.deviceMemory reports it (rounded, capped at 8). */
export const LOW_MEMORY_GB = 4;

export interface CandidateSignals {
  phone: boolean;
  /** null when the browser does not say (no Battery API: most desktops). */
  battery: { level: number; charging: boolean } | null;
  connection: { type?: string; effectiveType?: string; saveData?: boolean } | null;
  deviceMemory: number | null;
  hidden: boolean;
  /** Milliseconds since the user's last input in this tab. */
  idleMs: number;
  toolchainCached: boolean;
  wasm: boolean;
  /** The sandbox failed to start in this tab (markBootFailed). */
  bootFailed: boolean;
}

export function scoreCandidate(signals: CandidateSignals): Candidate {
  const { battery, connection, deviceMemory } = signals;
  const reasons: CandidateReason[] = [];
  if (signals.phone) reasons.push("phone");
  if (connection?.type === "cellular") reasons.push("cellular");
  if (connection?.saveData) reasons.push("save_data");
  if (battery && !battery.charging && battery.level < LOW_BATTERY) reasons.push("low_battery");
  if (deviceMemory !== null && deviceMemory < LOW_MEMORY_GB) reasons.push("low_memory");
  if (signals.hidden && signals.idleMs > RECENT_MS) reasons.push("hidden");
  if (!signals.wasm) reasons.push("no_wasm");
  if (signals.bootFailed) reasons.push("boot_failed");
  const toolchain_cached = signals.toolchainCached;
  if (reasons.length) return { candidate: false, score: 0, toolchain_cached, reasons };
  const power = !battery || battery.charging ? 15 : Math.round(battery.level * 15);
  const memory = deviceMemory === null ? 5 : deviceMemory >= 8 ? 10 : 5;
  const link = { "slow-2g": -20, "2g": -20, "3g": -10 }[connection?.effectiveType ?? ""] ?? 0;
  const score =
    40 + (toolchain_cached ? 25 : 0) + power + memory + (signals.hidden ? 0 : 10) + link;
  return {
    candidate: true,
    score: Math.max(0, Math.min(100, score)),
    toolchain_cached,
    reasons,
  };
}

export function sameCandidate(a: Candidate | null, b: Candidate | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ── Toolchain cache marker ─────────────────────────────────────────────────────

const TOOLCHAIN_KEY = "steddy.sandbox.toolchain";
/** This tab's watchers: `storage` fires only in the other tabs, so the setters tell these themselves. */
const markWatchers = new Set<() => void>();

/** A sandbox started in this tab (markToolchainCached): its browser can run one. */
let startedHere = false;

/** The sandbox started in this browser once: its runtime is in the HTTP cache. */
export function markToolchainCached(): void {
  startedHere = true;
  try {
    localStorage.setItem(TOOLCHAIN_KEY, "1");
  } catch {
    // Storage blocked: the tab reports an uncached toolchain, which only lowers its score.
  }
  markWatchers.forEach((changed) => changed());
}

function toolchainCached(): boolean {
  try {
    return localStorage.getItem(TOOLCHAIN_KEY) === "1";
  } catch {
    return false;
  }
}

// ── Boot failure ───────────────────────────────────────────────────────────────

let bootFailedHere = false;
/** What a call or start this tab gives up on says, so the model can tell the user. */
export const BOOT_FAILED =
  "the sandbox failed to start in this browser tab, so it is not tried here again: reload the page to try again";
/** The starts in flight in this tab (onBootFailed): each gives up once one fails. */
const bootFailedListeners = new Set<() => void>();

/**
 * The sandbox could not start in this tab (its frame or worker failed, or its start hung): the tab
 * stops being a candidate, so the server elects another host for its calls, and the starts still in
 * flight give up. Only while none has started here: one that did proves the browser runs it, so a
 * later failure is that workspace's. Kept for the tab's life and never stored: a reload tries again.
 */
export function markBootFailed(): void {
  if (bootFailedHere || startedHere) return;
  bootFailedHere = true;
  markWatchers.forEach((changed) => changed());
  bootFailedListeners.forEach((failed) => failed());
}

export function bootFailed(): boolean {
  return bootFailedHere;
}

/** Calls `failed` once the tab is marked (markBootFailed); returns the unsubscribe. */
export function onBootFailed(failed: () => void): () => void {
  bootFailedListeners.add(failed);
  return () => void bootFailedListeners.delete(failed);
}

// ── The watcher ────────────────────────────────────────────────────────────────

export interface CandidateWatcher {
  /** The report now, settled or not (sent with hello). */
  current(): Candidate;
  /** The report as it last settled (sent with presence after onChange). */
  reported(): Candidate;
  /** Calls back once the report has changed and then held for CANDIDATE_DEBOUNCE_MS (at once for a
   * failed boot). */
  onChange(callback: () => void): () => void;
}

export interface CandidateEnv {
  read(): CandidateSignals;
  /** Calls `changed` whenever a signal may have changed; returns the unsubscribe. */
  watch(changed: () => void): () => void;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export function watchCandidate(env: CandidateEnv): CandidateWatcher {
  let now = scoreCandidate(env.read());
  let reported = now;
  let timer: unknown = null;
  const listeners = new Set<() => void>();

  const settle = () => {
    timer = null;
    if (sameCandidate(now, reported)) return;
    reported = now;
    listeners.forEach((listener) => listener());
  };
  env.watch(() => {
    now = scoreCandidate(env.read());
    env.clearTimeout(timer);
    timer = null;
    if (sameCandidate(now, reported)) return;
    // A tab that knows it cannot host says so at once: every call sent to it meanwhile fails.
    if (now.reasons.includes("boot_failed") && !reported.reasons.includes("boot_failed")) settle();
    else timer = env.setTimeout(settle, CANDIDATE_DEBOUNCE_MS);
  });

  return {
    current: () => now,
    reported: () => reported,
    onChange(callback) {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
  };
}

// ── The browser's signals ──────────────────────────────────────────────────────

interface BatteryManager extends EventTarget {
  level: number;
  charging: boolean;
}
interface NetworkInformation extends EventTarget {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
}
type BrowserNavigator = Navigator & {
  getBattery?: () => Promise<BatteryManager>;
  connection?: NetworkInformation;
  deviceMemory?: number;
  userAgentData?: { mobile?: boolean };
};

const INPUT_EVENTS = ["pointerdown", "keydown", "wheel"] as const;
/** The hidden recheck fires this long after the tab stops being recent, so it reads it as gone. */
const RECHECK_SLACK_MS = 1_000;

/** This tab's signals and their change events (battery, link, visibility, input). */
export function browserCandidateEnv(): CandidateEnv {
  const nav = navigator as BrowserNavigator;
  let battery: BatteryManager | null = null;
  let lastInput = Date.now();
  const phone = () =>
    nav.userAgentData?.mobile ??
    (!!window.matchMedia?.("(pointer: coarse)").matches &&
      !window.matchMedia?.("(any-pointer: fine)").matches);

  return {
    read: () => ({
      phone: phone(),
      battery: battery && { level: battery.level, charging: battery.charging },
      connection: nav.connection ?? null,
      deviceMemory: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
      hidden: document.visibilityState === "hidden",
      idleMs: Date.now() - lastInput,
      toolchainCached: toolchainCached(),
      wasm: typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function",
      bootFailed: bootFailedHere,
    }),
    watch(changed) {
      let recheck: number | undefined;
      // A hidden tab stops being a candidate once it has been unused for RECENT_MS: wait until
      // then, and again if input while hidden made it recent once more.
      const awaitIdle = () => {
        window.clearTimeout(recheck);
        recheck = undefined;
        if (document.visibilityState !== "hidden") return;
        const idleMs = Date.now() - lastInput;
        if (idleMs > RECENT_MS) return;
        recheck = window.setTimeout(
          () => {
            changed();
            awaitIdle();
          },
          Math.max(0, RECENT_MS - idleMs) + RECHECK_SLACK_MS
        );
      };
      const visibility = () => {
        changed();
        awaitIdle();
      };
      const input = () => {
        lastInput = Date.now();
        if (document.visibilityState === "hidden") visibility();
      };
      const storage = (event: StorageEvent) => {
        if (event.key === TOOLCHAIN_KEY || event.key === null) changed();
      };
      document.addEventListener("visibilitychange", visibility);
      INPUT_EVENTS.forEach((type) => window.addEventListener(type, input, { passive: true }));
      window.addEventListener("storage", storage);
      markWatchers.add(changed);
      nav.connection?.addEventListener("change", changed);
      let gone = false;
      void nav
        .getBattery?.()
        .then((manager) => {
          if (gone) return;
          battery = manager;
          manager.addEventListener("levelchange", changed);
          manager.addEventListener("chargingchange", changed);
          changed();
        })
        .catch(() => undefined);
      return () => {
        gone = true;
        window.clearTimeout(recheck);
        document.removeEventListener("visibilitychange", visibility);
        INPUT_EVENTS.forEach((type) => window.removeEventListener(type, input));
        window.removeEventListener("storage", storage);
        markWatchers.delete(changed);
        nav.connection?.removeEventListener("change", changed);
        battery?.removeEventListener("levelchange", changed);
        battery?.removeEventListener("chargingchange", changed);
      };
    },
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
    clearTimeout: (handle) => window.clearTimeout(handle as number | undefined),
  };
}

// ── The report now ─────────────────────────────────────────────────────────────

let shared: CandidateWatcher | null = null;

/** This tab's one watcher of its browser signals, made on first use: the socket sends its reports. */
export function browserCandidate(): CandidateWatcher {
  shared ??= watchCandidate(browserCandidateEnv());
  return shared;
}

/**
 * This tab's candidate report now: the socket's watcher's (browserCandidate), so it says what the
 * server was last told or is about to be. Null when there is no browser to read (a worker, a test
 * without a DOM): such a context cannot host at all. A tab that may not host gets its report with
 * its reasons (`candidate: false`), which the server treats as null.
 */
export async function reportCandidate(): Promise<Candidate | null> {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  return browserCandidate().current();
}
